import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { CreditCard, AlertCircle, Loader2 } from 'lucide-react';
import { paymentApi } from '../../services/paymentService';
import { getPlatformSettlementCurrency } from '../../services/settlementCurrencyService';
import useStripeCheckout from '../../hooks/useStripeCheckout';
import StripeCheckoutStatus from '../shared/StripeCheckoutStatus';
import { useInternetStatus } from '../shared/InternetStatus';
import { isNetworkFailure } from '../../services/stripeConfigService';

// IMPORTANT:
// Don't initialize/load Stripe while offline, otherwise `stripe.js` fails to load and triggers an
// ugly "Uncaught runtime errors" overlay. We create `stripePromise` only when online.

const cardElementOptions = {
  style: {
    base: {
      fontSize: '15px',
      color: '#0a0a0a',
      fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, sans-serif',
      '::placeholder': {
        color: '#8a8a8a',
      },
    },
    invalid: {
      color: '#ef4444',
      iconColor: '#ef4444',
    },
  },
  hidePostalCode: true,
};

const PaymentForm = ({ 
  amount, 
  bookingId, // Legacy support - optional now
  bookingData, // New: booking data for new flow
  paymentType = 'deposit',
  onSuccess, 
  onError,
  metadata = {}
}) => {
  const { isOnline } = useInternetStatus();
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  
  // Use refs for callbacks to avoid dependency issues
  const onErrorRef = useRef(onError);
  const onSuccessRef = useRef(onSuccess);
  const metadataRef = useRef(metadata);
  const bookingDataRef = useRef(bookingData);
  
  // Update refs when props change
  useEffect(() => {
    onErrorRef.current = onError;
    onSuccessRef.current = onSuccess;
    metadataRef.current = metadata;
    bookingDataRef.current = bookingData;
  }, [onError, onSuccess, metadata, bookingData]);
  
  // Create a stable key from bookingData for dependency tracking
  const bookingDataKey = bookingData ? JSON.stringify({
    vendor_id: bookingData.vendor_id,
    service_id: bookingData.service_id,
    package_id: bookingData.package_id,
    event_date: bookingData.event_date,
    start_time: bookingData.start_time,
    total_amount: bookingData.total_amount,
  }) : null;

  // Create payment intent when online and booking details are ready
  useEffect(() => {
    setError(null);
    setClientSecret(null);

    if (!isOnline) {
      return undefined;
    }

    const createPaymentIntent = async () => {
      try {
        console.log('Creating payment intent...', { bookingData: bookingDataRef.current, bookingId, amount, paymentType });
        let settlementCurrency = 'USD';
        try {
          const settlement = await getPlatformSettlementCurrency();
          if (settlement?.currency) settlementCurrency = settlement.currency;
        } catch {
          // Backend still forces settlement currency; keep a display default.
        }

        let response;
        
        // Use new flow if bookingData is provided, otherwise use legacy flow
        if (bookingDataRef.current) {
          console.log('Using new flow with bookingData');
          response = await paymentApi.createPaymentIntentWithBooking({
            ...bookingDataRef.current,
            amount,
            currency: settlementCurrency,
            paymentType,
          });
        } else if (bookingId) {
          console.log('Using legacy flow with bookingId');
          // Legacy flow: requires existing booking
          response = await paymentApi.createPaymentIntent({
            bookingId,
            amount,
            currency: settlementCurrency,
            paymentType,
            metadata: metadataRef.current
          });
        } else {
          const errorMsg = 'Either bookingId or bookingData is required';
          console.error('Missing booking data:', { bookingData: bookingDataRef.current, bookingId });
          setError(errorMsg);
          if (onErrorRef.current) {
            onErrorRef.current(errorMsg);
          }
          return;
        }

        console.log('Payment intent response:', response);
        if (response.success && response.data?.client_secret) {
          console.log('Payment intent created successfully');
          setClientSecret(response.data.client_secret);
        } else {
          const errorMsg = response.message || response.error || 'Failed to initialize payment';
          console.error('Payment intent creation failed:', errorMsg, response);
          setError(errorMsg);
          if (onErrorRef.current) {
            onErrorRef.current(errorMsg);
          }
        }
      } catch (err) {
        console.error('Payment intent creation error:', err);
        const errorMessage = isNetworkFailure(err)
          ? 'Could not reach the server. Check your connection and try again.'
          : err.response?.data?.message ||
            err.response?.data?.error ||
            err.message ||
            'Failed to create payment intent. Please try again.';
        setError(errorMessage);
        if (onErrorRef.current) {
          onErrorRef.current(errorMessage);
        }
      }
    };

    // Only create payment intent if we have required data and amount
    // Remove !clientSecret check so it recreates when amount/paymentType changes
    if ((bookingId || bookingDataRef.current) && amount > 0) {
      createPaymentIntent();
    } else {
      console.log('Skipping payment intent creation:', { 
        hasBookingId: !!bookingId, 
        hasBookingData: !!bookingDataRef.current, 
        amount
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Note: metadata is NOT in dependencies - we don't want to recreate payment intent when user types in form fields
    // Only amount, paymentType, bookingId, and bookingData should trigger recreation
  }, [bookingId, bookingDataKey, amount, paymentType, isOnline]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);

    try {
      const billingDetails = {
        name: metadataRef.current?.cardholderName || 'Customer',
      };

      // Confirm payment with Stripe
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: billingDetails,
          },
        }
      );

      if (stripeError) {
        setError(stripeError.message);
        setProcessing(false);
        if (onErrorRef.current) {
          onErrorRef.current(stripeError.message);
        }
        return;
      }

      if (paymentIntent.status === 'succeeded') {
        // Confirm payment with backend (bookingId is optional now - booking will be created from metadata)
        try {
          const response = await paymentApi.confirmPayment(
            paymentIntent.id,
            bookingId || null // Optional - booking will be created from payment intent metadata if not provided
          );

          if (response.success) {
            if (onSuccessRef.current) {
              onSuccessRef.current({
                paymentIntentId: paymentIntent.id,
                bookingId: response.data?.booking?.id || bookingId, // Use booking ID from response if available
                amount,
                paymentType,
                ...response.data
              });
            }
          } else {
            setError(response.message || 'Payment confirmation failed');
            if (onErrorRef.current) {
              onErrorRef.current(response.message || 'Payment confirmation failed');
            }
          }
        } catch (err) {
          const errorMessage = err.response?.data?.message || 'Failed to confirm payment';
          setError(errorMessage);
          if (onErrorRef.current) {
            onErrorRef.current(errorMessage);
          }
        }
      } else {
        setError('Payment was not successful');
        if (onErrorRef.current) {
          onErrorRef.current('Payment was not successful');
        }
      }
    } catch (err) {
      const errorMessage = err.message || 'An error occurred during payment';
      setError(errorMessage);
      if (onErrorRef.current) {
        onErrorRef.current(errorMessage);
      }
    } finally {
      setProcessing(false);
    }
  };

  if (!clientSecret) {
    if (error) {
      return (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-5">
          <div className="mb-2 flex items-center text-red-600">
            <AlertCircle className="mr-2 h-5 w-5" />
            <span className="text-sm font-medium">Failed to create payment intent</span>
          </div>
          <p className="text-sm text-red-600">{error}</p>
          <button
            type="button"
            onClick={() => {
              setError(null);
              const createPaymentIntent = async () => {
                try {
                  let settlementCurrency = 'USD';
                  try {
                    const settlement = await getPlatformSettlementCurrency();
                    if (settlement?.currency) settlementCurrency = settlement.currency;
                  } catch {
                    // ignore
                  }
                  const response = await paymentApi.createPaymentIntent({
                    bookingId,
                    amount,
                    currency: settlementCurrency,
                    paymentType,
                    metadata
                  });

                  if (response.success && response.data.client_secret) {
                    setClientSecret(response.data.client_secret);
                  } else {
                    setError(response.message || 'Failed to initialize payment');
                  }
                } catch (err) {
                  setError(err.response?.data?.message || 'Failed to create payment intent');
                }
              };
              if (bookingId && amount > 0) {
                createPaymentIntent();
              }
            }}
            className="marketing-btn marketing-btn-ink mt-4"
          >
            Retry
          </button>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="mr-2 h-5 w-5 animate-spin text-m-ink-muted" />
        <span className="text-sm text-m-ink-muted">Initializing payment…</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <label className="auth-field-label">
          Card information
        </label>
        <div className="rounded-[1.25rem] border border-m-border-light bg-m-surface px-4 py-4 transition-colors focus-within:border-m-ink">
          <CardElement options={cardElementOptions} />
        </div>
        {error && (
          <div className="mt-2 flex items-center text-sm text-red-600">
            <AlertCircle className="mr-1 h-4 w-4" />
            <span>{error}</span>
          </div>
        )}
      </div>

      <div>
        <label className="auth-field-label" htmlFor="booking-cardholder-name">
          Cardholder name
        </label>
        <input
          id="booking-cardholder-name"
          type="text"
          placeholder="John Doe"
          value={metadata.cardholderName || ''}
          onChange={(e) => {
            if (metadata.onCardholderNameChange) {
              metadata.onCardholderNameChange(e.target.value);
            }
          }}
          className="auth-input"
        />
      </div>

      <div className="rounded-2xl border border-m-border-light bg-m-canvas-light p-4">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm text-m-ink-muted">Payment amount</span>
          <span className="text-lg font-medium text-m-ink">
            ${Number(amount).toFixed(2)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-m-ink-muted">Payment type</span>
          <span className="text-sm font-medium text-m-ink">
            {paymentType === 'full_payment'
              ? 'Full payment'
              : `Deposit (${bookingData?.payment_percentage ?? 50}%)`}
          </span>
        </div>
      </div>

      <button
        type="submit"
        disabled={!stripe || processing}
        className="marketing-btn marketing-btn-ink w-full gap-2 disabled:opacity-50"
      >
        {processing ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            Processing payment…
          </>
        ) : (
          <>
            <CreditCard className="h-5 w-5" />
            Pay ${Number(amount).toFixed(2)}
          </>
        )}
      </button>

      <p className="text-center text-xs text-m-ink-faint">
        Your payment is secured by Stripe. We never store your card details.
      </p>
    </form>
  );
};

const StripePaymentForm = ({ 
  amount, 
  bookingId, // Legacy support - optional now
  bookingData, // New: booking data for new flow
  paymentType = 'deposit',
  onSuccess, 
  onError,
  metadata = {}
}) => {
  const { isOnline, isChecking, stripePromise, loading, error, retry } = useStripeCheckout();

  const elementsKey = useMemo(() => {
    return `${bookingId || 'new'}-${amount}-${paymentType}`;
  }, [bookingId, amount, paymentType]);

  const gate = (
    <StripeCheckoutStatus
      isOnline={isOnline}
      isChecking={isChecking}
      loading={loading}
      error={error}
      onRetry={retry}
      loadingMessage="Loading payment settings..."
    />
  );

  if (!isOnline || loading || error) {
    return gate;
  }

  return (
    <Elements stripe={stripePromise} key={elementsKey}>
      <PaymentForm
        amount={amount}
        bookingId={bookingId}
        bookingData={bookingData}
        paymentType={paymentType}
        onSuccess={onSuccess}
        onError={onError}
        metadata={metadata}
      />
    </Elements>
  );
};

export default StripePaymentForm;

